import { Theme } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useContext, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import ConnectIcon from '@/assets/icons/connect-icon.svg?react';
import ThirdPartyAppIconDark from '@/assets/icons/third-party-app-dark.svg?react';
import ThirdPartyAppIcon from '@/assets/icons/third-party-app.svg?react';
import DynamicT from '@/shared/components/DynamicT';
import { layoutClassNames } from '@/utils/consts';

import styles from './index.module.scss';

export type Props = {
  readonly className?: string;
  readonly logo?: Nullable<string>;
  readonly thirdPartyLogo?: Nullable<string>;
  readonly headline?: TFuncKey;
  readonly headlineInterpolation?: Record<string, unknown>;
};

const BrandingHeader = ({
  logo,
  thirdPartyLogo,
  headline,
  headlineInterpolation,
  className,
}: Props) => {
  const { theme } = useContext(PageContext);
  /**
   * The third-party logo is a remote asset the client declares (e.g. a CIMD client's `logo_uri`),
   * loaded with no user interaction: `no-referrer` keeps it from carrying the tenant origin to
   * that host, and a load failure falls back to the third-party application icon so the header
   * keeps its shape.
   */
  const [isThirdPartyLogoBroken, setIsThirdPartyLogoBroken] = useState(false);
  const FallbackThirdPartyLogo = theme === Theme.Light ? ThirdPartyAppIcon : ThirdPartyAppIconDark;
  /*
   * TDA: this row is now ONLY the third-party relationship — "their app ⟷ us" —
   * on the consent screen.
   *
   * Our own logo standing alone used to be drawn here too. It moved to
   * `Layout/AppLayout`, which draws the brand lockup above `<Outlet />` for every
   * screen rather than only the four that have a `BrandingHeader`. Drawing it
   * here as well would put the same mark on screen twice.
   *
   * The container carries `logto_third-party-branding-header` while this row is
   * up, which is how that lockup knows to stand down: our logo is already in the
   * row, next to theirs.
   */
  const hasThirdPartyLogo = Boolean(thirdPartyLogo);
  const shouldConnectSvg = Boolean(thirdPartyLogo && logo);

  return (
    <div
      className={classNames(
        styles.container,
        className,
        hasThirdPartyLogo && layoutClassNames.thirdPartyBrandingHeader
      )}
    >
      {hasThirdPartyLogo && (
        <div className={styles.logoWrapper}>
          {thirdPartyLogo &&
            (isThirdPartyLogoBroken ? (
              <FallbackThirdPartyLogo className={classNames(styles.logo, styles.thirdPartyLogo)} />
            ) : (
              <img
                className={classNames(styles.logo, styles.thirdPartyLogo)}
                alt="third party logo"
                src={thirdPartyLogo}
                referrerPolicy="no-referrer"
                onError={() => {
                  setIsThirdPartyLogoBroken(true);
                }}
              />
            ))}
          {shouldConnectSvg && <ConnectIcon className={styles.connectIcon} />}
          {logo && <img className={styles.logo} alt="app logo" src={logo} />}
        </div>
      )}

      {/* TDA: Plane stacks a bold statement over a muted line. `tagline` is the
          bold one; Logto's own headline becomes the muted second line, so the
          translated string keeps doing real work instead of being replaced. */}
      {headline && (
        <>
          <div className={styles.tagline}>
            <DynamicT forKey="description.tdx_tagline" />
          </div>
          <div className={styles.headline}>
            <DynamicT forKey={headline} interpolation={headlineInterpolation} />
          </div>
        </>
      )}
    </div>
  );
};

export default BrandingHeader;
